import { useEffect, useMemo, useState } from 'react';
import ProductTierShowcase from './ProductTierShowcase';
import { CloseIcon } from './Icons';
import { useCart } from '../context/CartContext';
import { naturalJuices } from '../data/menuData';
import { formatPrice } from '../utils/formatPrice';

function MealComposerModal({ product, onClose }) {
  const { addItem } = useCart();
  const [included, setIncluded] = useState(() =>
    Object.fromEntries(product.includedIngredients.map((ing) => [ing.id, true]))
  );
  const [extras, setExtras] = useState({});
  const [selectedDrinks, setSelectedDrinks] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');

  const showDrinkComplements = product.category !== 'boissons';

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const extrasTotal = useMemo(
    () =>
      product.extraIngredients.reduce(
        (sum, ing) => sum + (extras[ing.id] ? ing.price : 0),
        0
      ),
    [extras, product.extraIngredients]
  );

  const drinksTotal = useMemo(
    () =>
      naturalJuices.reduce(
        (sum, drink) => sum + (selectedDrinks[drink.id] ? drink.price : 0),
        0
      ),
    [selectedDrinks]
  );

  const unitPrice = product.basePrice + extrasTotal + drinksTotal;

  const selectedIncluded = product.includedIngredients.filter((ing) => included[ing.id]);
  const removedIncluded = product.includedIngredients.filter((ing) => !included[ing.id]);
  const selectedExtras = product.extraIngredients.filter((ing) => extras[ing.id]);
  const drinksSelection = naturalJuices.filter((drink) => selectedDrinks[drink.id]);

  const toggleIncluded = (id) => {
    setIncluded((current) => ({ ...current, [id]: !current[id] }));
  };

  const toggleExtra = (id) => {
    setExtras((current) => ({ ...current, [id]: !current[id] }));
  };

  const toggleDrink = (id) => {
    setSelectedDrinks((current) => ({ ...current, [id]: !current[id] }));
  };

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      name: product.name,
      image: product.image,
      quantity,
      unitPrice,
      lineTotal: unitPrice * quantity,
      selectedIncluded,
      removedIncluded,
      selectedExtras,
      selectedDrinks: drinksSelection,
      notes: notes.trim(),
    });
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="meal-composer-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="composer-title"
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="Fermer">
          <CloseIcon size={22} />
        </button>

        <div className="composer-layout">
          <aside className="composer-preview-panel">
            <ProductTierShowcase product={product} />
          </aside>

          <div className="composer-main">
            <div className="composer-header">
              <div>
                <h2 id="composer-title" className="composer-title">{product.name}</h2>
                <p className="composer-desc">{product.desc}</p>
                <p className="composer-base-price">Prix de base : {formatPrice(product.basePrice)}</p>
              </div>
            </div>

            <div className="composer-body">
          {product.includedIngredients.length > 0 && (
            <section className="composer-section">
              <h3 className="composer-section-title">Ingrédients inclus</h3>
              <p className="composer-section-hint">Décochez ce que vous ne souhaitez pas.</p>
              <ul className="composer-ingredient-list">
                {product.includedIngredients.map((ingredient) => (
                  <li key={ingredient.id}>
                    <label className="composer-ingredient">
                      <input
                        type="checkbox"
                        checked={included[ingredient.id]}
                        onChange={() => toggleIncluded(ingredient.id)}
                      />
                      <span>{ingredient.name}</span>
                      <span className="composer-ingredient-tag">Inclus</span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {product.extraIngredients.length > 0 && (
            <section className="composer-section">
              <h3 className="composer-section-title">Suppléments</h3>
              <p className="composer-section-hint">Ajoutez les ingrédients de votre choix.</p>
              <ul className="composer-ingredient-list">
                {product.extraIngredients.map((ingredient) => (
                  <li key={ingredient.id}>
                    <label className="composer-ingredient">
                      <input
                        type="checkbox"
                        checked={!!extras[ingredient.id]}
                        onChange={() => toggleExtra(ingredient.id)}
                      />
                      <span>{ingredient.name}</span>
                      <span className="composer-ingredient-price">+{formatPrice(ingredient.price)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {showDrinkComplements && (
            <section className="composer-section composer-section-drinks">
              <h3 className="composer-section-title">Accompagner d&apos;un jus naturel</h3>
              <p className="composer-section-hint">
                100% naturel, sans conservateur — parfait en complément de votre repas.
              </p>
              <ul className="composer-ingredient-list">
                {naturalJuices.map((drink) => (
                  <li key={drink.id}>
                    <label className="composer-ingredient composer-drink-option">
                      <input
                        type="checkbox"
                        checked={!!selectedDrinks[drink.id]}
                        onChange={() => toggleDrink(drink.id)}
                      />
                      <span className="composer-drink-name">{drink.name}</span>
                      <span className="composer-drink-desc">{drink.desc}</span>
                      <span className="composer-ingredient-price">+{formatPrice(drink.price)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="composer-section">
            <h3 className="composer-section-title">Instructions</h3>
            <textarea
              className="composer-notes"
              placeholder="Ex. : bien cuit, sans oignon, sauce à part…"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
            />
          </section>
            </div>

            <div className="composer-footer">
          <div className="composer-quantity">
            <span className="composer-quantity-label">Quantité</span>
            <div className="quantity-control">
              <button
                type="button"
                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                aria-label="Diminuer la quantité"
              >
                −
              </button>
              <span>{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((value) => value + 1)}
                aria-label="Augmenter la quantité"
              >
                +
              </button>
            </div>
          </div>

          <button type="button" className="btn btn-primary composer-add-btn" onClick={handleAddToCart}>
            Ajouter au panier — {formatPrice(unitPrice * quantity)}
          </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MealComposerModal;
